<template>
  <div class="modal" @click.self="close">
    <button @click="closeModal()" class="modal__Overlay"></button>
    <success-prompt :updated="updated" :message="prompt.success"></success-prompt>

    <div class="modal__Inner--Builder">
      <section class="modal__Post">
        <header class="modal__Header">
          <h1 class="modal__Title">{{ $store.getters.getStringTranslation('editQuiz') }}</h1>

          <div class="modal__Meta">
            <ul>
              <li class="modal__MetaItem--Button">
                <button @click="update()" :disabled="updated === true" class="button--Small">
                  {{ prompt.save }}
                </button>
              </li>
              <li class="modal__MetaItem">
                <button @click="closeModal()" class="modal__MetaLink">
                  <icon name="times" />
                </button>
              </li>
            </ul>
          </div>
        </header>

        <error-prompt class="error--Modal" :errors="errors"></error-prompt>
        <loader class="imageLoader" :loading="loading || updating" />

        <div class="modal__Scrollable" id="editorModal">
          <template v-if="loading === false">
            <div class="modal__BuilderWrapper">
              <div class="modal__Editor--Builder">
                <p>
                  <label class="formLabel" for="editTitle">{{ $store.getters.getStringTranslation('title') }}</label>
                  <input
                    required
                    v-model="getTranslationObject(item.l10n).title"
                    type="text"
                    class="formInput"
                    id="editTitle"
                  />
                </p>
                <nav>
                  <ul class="modal__BuilderChangeEditor--Type">
                    <li>
                      <button @click="layout = 'outcomes'" :class="{ 'is-active': layout === 'outcomes' }">
                        {{ $store.getters.getStringTranslation('outcomes') }}
                        <span>{{ getTranslationObject(item.l10n).outcomes.length }}</span>
                      </button>
                    </li>
                    <li>
                      <button @click="layout = 'sections'" :class="{ 'is-active': layout === 'sections' }">
                        {{ $store.getters.getStringTranslation('sections') }}
                        <span>{{ getTranslationObject(item.l10n).sections.length }}</span>
                      </button>
                    </li>
                    <li>
                      <button @click="layout = 'json'" :class="{ 'is-active': layout === 'json' }">
                        {{ $store.getters.getStringTranslation('data') }}
                      </button>
                    </li>
                  </ul>
                </nav>
                <section class="modal__Builder" v-if="layout === 'outcomes'">
                  <div class="modal__BuilderBlockWrapper">
                    <div
                      class="modal__BuilderHelper--Section"
                      v-if="getTranslationObject(item.l10n).outcomes.length === 0"
                    >
                      <p>
                        <icon name="chart-area" /><br />
                        Outcomes represent the results of the quiz. Each question can add score to one or more outcomes.
                      </p>
                    </div>
                    <template v-else>
                      <div v-for="(outcome, index) in getTranslationObject(item.l10n).outcomes" :key="index">
                        <ul class="modal__BuilderOptions">
                          <li class="modal__BuilderOptionsItem--Title">Outcome {{ index + 1 }}</li>
                          <li class="modal__BuilderOptionsItem--Delete">
                            <button
                              class="modal__BuilderButton"
                              @click="removeElement(getTranslationObject(item.l10n).outcomes, index)"
                            >
                              <icon name="trash-alt"></icon>
                              <span>Remove outcome</span>
                            </button>
                          </li>
                        </ul>
                        <div class="modal__BuilderBlockWrapper--Repeater">
                          <p>
                            <label :for="`outcome-name-${index}`" class="formLabel">Outcome name</label>
                            <input
                              type="text"
                              class="formInput"
                              :id="`string-field-name-${index}`"
                              v-model="outcome.name"
                            />
                          </p>
                          <p>
                            <label :for="`outcome-type-${index}`" class="formLabel">Outcome type</label>
                            <select :id="`outcome-type-${index}`" class="formSelect" v-model="outcome.type" required>
                              <option value="chart">Radar chart</option>
                              <option value="summary">Score summary</option>
                            </select>
                            <small class="note"
                              >Score summary will return a number while a radar chart requires additional fields to be
                              set from which the chart data will be generated.</small
                            >
                          </p>
                          <template v-if="outcome.type === 'chart'">
                            <p>
                              <label :for="`string-field-max-score-${index}`" class="formLabel"
                                >Maximum score on chart</label
                              >
                              <input
                                type="text"
                                class="formInput"
                                :id="`outcome-max-score-${index}`"
                                v-model.number="outcome.max_score"
                              />
                            </p>
                            <div v-for="(field, index) in outcome.fields" :key="field.id">
                              <ul class="modal__BuilderOptions">
                                <li class="modal__BuilderOptionsItem--Title">Field {{ index + 1 }}</li>
                                <li class="modal__BuilderOptionsItem--Delete">
                                  <button class="modal__BuilderButton" @click="removeElement(outcome.fields, index)">
                                    <icon name="trash-alt"></icon>
                                    <span>Remove field</span>
                                  </button>
                                </li>
                              </ul>
                              <div class="modal__BuilderBlockWrapper--Repeater">
                                <p>
                                  <label :for="`outcome-field-name-${index}`" class="formLabel"
                                    >Chart field label</label
                                  >
                                  <input
                                    type="text"
                                    class="formInput"
                                    :id="`outcome-field-label-${index}`"
                                    v-model="field.label"
                                  />
                                  <small class="note">The name of one vertical within the chart.</small>
                                </p>
                              </div>
                            </div>
                            <p><button class="button" @click="addOutcomeField(outcome)">Add field</button></p>
                          </template>
                        </div>
                      </div>
                    </template>
                    <p>
                      <button
                        class="button"
                        :class="{ 'button--Center': getTranslationObject(item.l10n).outcomes.length === 0 }"
                        @click="addOutcome()"
                      >
                        Add outcome
                      </button>
                    </p>
                    <div
                      class="modal__BuilderButtons--Copy"
                      v-if="
                        !getTranslationObject(item.l10n).outcomes ||
                        (getTranslationObject(item.l10n).outcomes &&
                          getTranslationObject(item.l10n).outcomes.length === 0)
                      "
                    >
                      <template v-for="translation in langs.filter((t) => t.toLowerCase() !== lang)">
                        <button
                          class="button--Accent"
                          :key="translation.id"
                          v-if="
                            getTranslationObject(item.l10n, translation.toLowerCase()) &&
                            getTranslationObject(item.l10n, translation.toLowerCase()).outcomes &&
                            getTranslationObject(item.l10n, translation.toLowerCase()).outcomes.length > 0
                          "
                          @click="copyBlocks(translation, 'outcomes')"
                        >
                          <icon name="sign-in" weight="solid" /> Overwrite from {{ translation }}
                        </button>
                      </template>
                    </div>
                  </div>
                </section>
                <section class="modal__Builder" v-if="layout === 'sections'">
                  <div class="modal__BuilderBlockWrapper">
                    <div
                      class="modal__BuilderHelper--Section"
                      v-if="getTranslationObject(item.l10n).sections.length === 0"
                    >
                      <p>
                        <icon name="question-circle" /><br />
                        Sections are the backbone of your survey - they can either represent a deck of text or a deck of
                        questions and answers.
                      </p>
                    </div>
                    <template v-else>
                      <div v-for="(section, index) in getTranslationObject(item.l10n).sections" :key="index">
                        <ul class="modal__BuilderOptions">
                          <li class="modal__BuilderOptionsItem--Title">Section {{ index + 1 }}</li>
                          <li class="modal__BuilderOptionsItem--Delete">
                            <button
                              class="modal__BuilderButton"
                              @click="removeElement(getTranslationObject(item.l10n).sections, index)"
                            >
                              <icon name="trash-alt"></icon>
                              <span>Remove section</span>
                            </button>
                          </li>
                        </ul>
                        <div class="modal__BuilderBlockWrapper--Repeater">
                          <p>
                            <label :for="`section-name-${index}`" class="formLabel">Section name</label>
                            <input type="text" class="formInput" :id="`section-name-${index}`" v-model="section.name" />
                          </p>
                          <div>
                            <label :for="`section-conditional-compare-${index}`" class="formLabel"
                              >Conditional display</label
                            >
                            <!-- <p>
                                <select
                                  class="formSelect"
                                  v-model="section.conditionals_compare"
                                  v-if="section.conditionals.length > 0"
                                >
                                  <option value="&&">AND</option>
                                  <option value="||">OR</option>
                                </select>
                              </p> -->
                            <div
                              class="formFlex"
                              v-for="(conditional, conditionalIndex) in section.conditionals"
                              :key="`section-${index}-conditional-${conditionalIndex}`"
                            >
                              <select class="formSelect" v-model="conditional.first" required>
                                <option value="">First outcome</option>
                                <option
                                  v-for="outcome in getTranslationObject(item.l10n).outcomes"
                                  :value="outcome.id"
                                  :key="outcome.id"
                                >
                                  {{ outcome.name }}
                                </option>
                              </select>
                              <select class="formSelect" v-model="conditional.operator" required>
                                <option value="">Operator</option>
                                <option
                                  :value="operatorIndex"
                                  v-for="(operator, operatorIndex) in operators"
                                  :key="`section-${index}-operator-${operatorIndex}`"
                                >
                                  {{ operatorIndex }}
                                </option>
                              </select>
                              <select class="formSelect" v-model="conditional.second" required>
                                <option value="">Second outcome</option>
                                <option
                                  v-for="outcome in getTranslationObject(item.l10n).outcomes"
                                  :value="outcome.id"
                                  :key="outcome.id"
                                >
                                  {{ outcome.name }}
                                </option>
                              </select>
                              <button
                                class="button--DeleteAccount"
                                @click="removeElement(section.conditionals, conditionalIndex)"
                              >
                                <icon weight="regular" name="trash-alt" />
                              </button>
                            </div>
                            <button
                              class="button button--XSmall"
                              @click="section.conditionals.push({ operator: '', first: '', second: '' })"
                            >
                              + Add conditional
                            </button>
                          </div>
                          <p>
                            <label :for="`section-color-${index}`" class="formLabel">Section color</label>
                            <input
                              type="color"
                              class="formInput--Color"
                              :id="`section-color-${index}`"
                              v-model="section.background_color"
                            />
                          </p>
                          <p>
                            <input
                              class="formCheckbox__Checkbox"
                              type="checkbox"
                              :name="`section-${index}-show-pdf`"
                              :id="`section-${index}-show-pdf`"
                              v-model="section.show_pdf"
                            />
                            <label class="formCheckbox__Label" :for="`section-${index}-show-pdf`"
                              >Show download PDF button</label
                            >
                          </p>
                          <div class="formSection__Row--Close">
                            <label :for="`section-outcome-${index}`" class="formLabel"
                              >Display specific charts or summary score</label
                            >
                            <p v-for="outcome in getTranslationObject(item.l10n).outcomes" :key="outcome.id">
                              <input
                                class="formCheckbox__Checkbox"
                                type="checkbox"
                                :name="`section-${index}-outcome-checkbox-${outcome.id}`"
                                :id="`section-${index}-outcome-checkbox-${outcome.id}`"
                                :value="{ id: outcome.id }"
                                v-model="section.outcomes"
                              />
                              <label
                                class="formCheckbox__Label"
                                :for="`section-${index}-outcome-checkbox-${outcome.id}`"
                                ><small>{{ outcome.type }}</small
                                >{{ outcome.name }}</label
                              >
                            </p>
                            <small class="note"
                              >Optional. Will display the chart on the section deck too if the section contains
                              questions with outcomes.</small
                            >
                          </div>
                          <p>
                            <label :for="`section-content-${index}`" class="formLabel">Content</label>
                            <text-editor v-model="section.text" />
                          </p>
                          <div v-for="(question, questionIndex) in section.questions" :key="question.id">
                            <ul class="modal__BuilderOptions">
                              <li class="modal__BuilderOptionsItem--Title">Question {{ questionIndex + 1 }}</li>
                              <li class="modal__BuilderOptionsItem--Delete">
                                <button
                                  class="modal__BuilderButton"
                                  @click="removeElement(section.questions, questionIndex)"
                                >
                                  <icon name="trash-alt"></icon>
                                  <span>Remove question</span>
                                </button>
                              </li>
                            </ul>
                            <div class="modal__BuilderBlockWrapper--Repeater">
                              <p>
                                <label :for="`question-name-${questionIndex}`" class="formLabel">Question</label>
                                <input
                                  type="text"
                                  class="formInput"
                                  :id="`question-name-${questionIndex}`"
                                  v-model="question.name"
                                />
                                <small class="note">The question you are about to ask your visitor.</small>
                              </p>
                              <div
                                v-for="(outcome, outcomeIndex) in getTranslationObject(item.l10n).outcomes"
                                :key="outcome.id"
                                class="formSection__Row--Close"
                              >
                                <label class="formLabel">{{ outcome.name }}</label>
                                <template v-if="outcome.type === 'summary'">
                                  <input
                                    class="formCheckbox__Checkbox"
                                    type="checkbox"
                                    :name="`question-outcome-summary-${index}-${questionIndex}-${outcomeIndex}`"
                                    :id="`question-outcome-summary-${index}-${questionIndex}-${outcomeIndex}`"
                                    :value="{ id: outcome.id }"
                                    v-model="question.outcomes"
                                  />
                                  <label
                                    class="formCheckbox__Label"
                                    :for="`question-outcome-summary-${index}-${questionIndex}-${outcomeIndex}`"
                                    >Link summary score to outcome</label
                                  >
                                </template>
                                <template v-else>
                                  <p v-for="(field, fieldIndex) in outcome.fields" :key="field.id">
                                    <input
                                      class="formCheckbox__Checkbox"
                                      type="checkbox"
                                      :name="`question-outcome-field-${outcomeIndex}`"
                                      :id="`question-outcome-field-${index}-${questionIndex}-${outcomeIndex}-${fieldIndex}`"
                                      :value="{ id: outcome.id, field_id: field.id }"
                                      v-model="question.outcomes"
                                    />
                                    <label
                                      class="formCheckbox__Label"
                                      :for="`question-outcome-field-${index}-${questionIndex}-${outcomeIndex}-${fieldIndex}`"
                                      >{{ field.label }}</label
                                    >
                                  </p>
                                </template>
                              </div>
                              <ul class="modal__BuilderResources--Slim" v-if="question.answers.length > 0">
                                <li
                                  class="modal__BuilderResourcesItem"
                                  v-for="(answer, answerIndex) in question.answers"
                                  :key="answer.id"
                                >
                                  <button
                                    class="modal__BuilderResourcesIcon"
                                    @click="moveResource(answerIndex, answerIndex - 1, question.answers)"
                                  >
                                    <icon name="arrow-up"></icon>
                                  </button>
                                  <button
                                    class="modal__BuilderResourcesIcon"
                                    @click="moveResource(answerIndex, answerIndex + 1, question.answers)"
                                  >
                                    <icon name="arrow-down"></icon>
                                  </button>
                                  <input
                                    type="text"
                                    class="modal__BuilderResourcesInput"
                                    v-model="answer.label"
                                    placeholder="Enter your answer here"
                                  />
                                  <button
                                    class="modal__BuilderResourcesIcon--Delete"
                                    @click="removeElement(question.answers, answerIndex)"
                                  >
                                    <icon weight="regular" name="times"></icon>
                                  </button>
                                </li>
                              </ul>
                              <p><button class="button" @click="addQuestionAnswer(question)">Add answer</button></p>
                            </div>
                          </div>
                          <p><button class="button" @click="addSectionQuestion(section)">Add question</button></p>
                        </div>
                      </div>
                    </template>
                    <p>
                      <button
                        class="button"
                        :class="{ 'button--Center': getTranslationObject(item.l10n).sections.length === 0 }"
                        @click="addSection()"
                      >
                        Add section
                      </button>
                    </p>
                    <div
                      class="modal__BuilderButtons--Copy"
                      v-if="
                        !getTranslationObject(item.l10n).sections ||
                        (getTranslationObject(item.l10n).sections &&
                          getTranslationObject(item.l10n).sections.length === 0)
                      "
                    >
                      <template v-for="translation in langs.filter((t) => t.toLowerCase() !== lang)">
                        <button
                          class="button--Accent"
                          :key="translation.id"
                          v-if="
                            getTranslationObject(item.l10n, translation.toLowerCase()) &&
                            getTranslationObject(item.l10n, translation.toLowerCase()).sections &&
                            getTranslationObject(item.l10n, translation.toLowerCase()).sections.length > 0
                          "
                          @click="copyBlocks(translation, 'sections')"
                        >
                          <icon name="sign-in" weight="solid" /> Overwrite from {{ translation }}
                        </button>
                      </template>
                    </div>
                  </div>
                </section>
                <section class="modal__Builder" v-if="layout === 'json'">
                  <div class="modal__BuilderHelper" v-if="getTranslationObject(item.l10n).outcomes.length === 0">
                    <p>
                      <icon name="exclamation-circle" /><br />
                      This is you quiz's raw data until we enable everyone to edit/save the quiz directly to the
                      database. Until then, you are free to download the quiz's data from the button above and paste
                      it's contents here if you wish to continue building the quiz.
                    </p>
                  </div>
                  <vue-json-editor v-model="item" :show-btns="false"></vue-json-editor>
                </section>
              </div>
              <aside class="modal__Post--Sidebar">
                <div class="cardPreview">
                  <div class="cardPreview__Columns--Blocks">
                    <div class="cardPreview__Column">
                      <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('languages') }}</h2>
                      <ul class="cardPreview__Meta">
                        <li v-for="translation in langs" :key="translation.id">
                          <!-- Regular link for exising translation -->
                          <template v-if="getTranslationObject(item.l10n, translation.toLowerCase()) !== undefined">
                            <router-link
                              class="cardPreview__MetaLink"
                              :to="{ query: { lang: translation.toLowerCase() } }"
                              >{{ translation }}
                              <icon
                                name="check"
                                v-if="getTranslationObject(item.l10n, translation.toLowerCase()).title.length > 0"
                              ></icon>
                            </router-link>
                            <button
                              v-if="item.l10n !== undefined && item.l10n.length > 1"
                              class="cardPreview__MetaPopup"
                              @click="removeTranslation(translation, true, false)"
                            >
                              <icon name="times"></icon> Remove
                            </button>
                          </template>
                          <!-- New translation -->
                          <button class="cardPreview__MetaLink--Add" @click="addTranslation(translation, false)" v-else>
                            {{ translation }} <icon name="plus" weight="solid"></icon>
                          </button>
                        </li>
                      </ul>
                    </div>

                    <div class="formSection__Row">
                      <label class="formLabel">{{ $store.getters.getStringTranslation('publishTo') }}</label>
                      <p v-for="(website, index) in websites" :key="`website-api-key-${index}`">
                        <input
                          class="formCheckbox__Checkbox"
                          type="checkbox"
                          name="publish-website-to"
                          :id="`publish-website-${index}`"
                          :value="website.api_key"
                          v-model="item.api_keys"
                        />
                        <label class="formCheckbox__Label--Short" :for="`publish-website-${index}`">{{
                          website.url.replace('https://www.', '').replace('http://www.', '')
                        }}</label>
                      </p>
                      <p>
                        <image-upload :required="false" v-model="item.contents.thumbnail_urls"></image-upload>
                      </p>
                      <p>
                        <label class="formLabel"
                          >{{ $store.getters.getStringTranslation('post') }}
                          {{ $store.getters.getStringTranslation('status') }}</label
                        >
                        <select class="formSelect" v-model="item.status">
                          <option v-for="status in statuses" :value="status" :key="status.id">
                            {{ $store.getters.getStringTranslation(status) }}
                          </option>
                        </select>
                      </p>
                      <p>
                        <search-author
                          :multiple="true"
                          :lang="lang"
                          :authors="item.authors"
                          v-model="item.authors_id"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ISOlanguages } from '@/router/globals.js'
import HTTP from '@/http-common.js'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'

import SearchAuthor from '@/components/editor/blocks/SearchAuthor.vue'
import GetTranslationObject from '@/components/mixins/GetTranslationObject.js'
import GetProperty from '@/components/mixins/GetProperty.js'
import DisableItem from '@/components/mixins/DisableItem.js'
import ManageTranslations from '@/components/mixins/ManageTranslations.js'
import Capitalize from '@/components/mixins/Capitalize.js'
import UpdatePost from '@/components/mixins/UpdatePost.js'
import MoveResource from '@/components/mixins/MoveResource.js'
import Operators from '@/components/mixins/Operators.js'

import ImageUpload from '@/components/editor/ImageUpload.vue'
import Loader from '@/components/partials/Loader.vue'
import TextEditor from '@/components/editor/TextEditor.vue'
import vueJsonEditor from 'vue-json-editor'

import ErrorPrompt from '@/components/editor/ErrorPrompt.vue'
import SuccessPrompt from '@/components/editor/SuccessPrompt.vue'

export default {
  name: 'EditQuiz',
  components: {
    ImageUpload,
    Loader,
    TextEditor,
    vueJsonEditor,
    ErrorPrompt,
    SuccessPrompt,
    SearchAuthor,
  },
  props: ['propLanguage', 'queryParams', 'types'],
  mixins: [
    GetProperty,
    GetTranslationObject,
    DisableItem,
    ManageTranslations,
    Capitalize,
    UpdatePost,
    MoveResource,
    Operators,
  ],
  data() {
    return {
      loading: true,
      updating: false,
      updated: false,
      errors: null,
      layout: 'outcomes',
      item: {
        author_id: null,
        api_keys: this.currentWebsite,
        type: 'quiz',
        tag_ids: [],
        tags: [],
        status: 'published',
        l10n: [
          {
            title: '',
            status: 'published',
            lang: 'en',
            outcomes: [],
            sections: [],
          },
        ],
        contents: {},
        start_datetime: moment().format(),
        end_datetime: moment().format(),
      },
      author: '',
      operators: {
        '>': (a, b) => a > b,
        '>=': (a, b) => a >= b,
        '<': (a, b) => a < b,
        '<=': (a, b) => a <= b,
        '===': (a, b) => a === b,
        '!==': (a, b) => a !== b,
      },
      prompt: {
        save: 'Save',
        success: '',
      },
      statuses: this.$store.state.types.statuses,
    }
  },
  computed: {
    lang() {
      if (this.$route.query.lang !== undefined) {
        return this.$route.query.lang
        //  If not, retrieve language from prop
      } else {
        return this.propLanguage
      }
    },
    langs() {
      return ISOlanguages
    },
    parent() {
      return {
        path: this.$route.matched.find((x) => x.parent !== undefined).parent.path,
        query: {
          search: JSON.stringify(this.queryParams),
        },
      }
    },
    currentWebsite() {
      return this.$store.state.websites.current || this.$store.state.accounts.api_keys
    },
    websites() {
      return { ...this.$store.state.accounts.org.websites }
    },
  },
  created() {
    this.author = this.$store.state.accounts.user
    const id = this.$route.params.id
    if (id !== undefined) {
      HTTP.post(`resource/${id}`, {
        api_keys: this.$store.state.accounts.api_keys,
      })
        .then((response) => {
          this.item = response.data
          this.loading = false
          this.item.api_keys = response.data.websites.map((website) => website.api_key)
          this.prompt.save = 'Save'
          this.prompt.success = 'Success! Quiz updated.'
        })
        .catch((e) => {
          this.errors = e.response ? e.response.data : console.error(e)
          this.loading = false
        })
    } else {
      this.loading = false
      this.prompt.save = 'Create'
      this.prompt.success = 'Success! Quiz created.'
    }
  },
  methods: {
    addOutcome() {
      this.getTranslationObject(this.item.l10n).outcomes.push({
        id: uuidv4(),
        name: '',
        max_score: 0,
        fields: [],
      })
    },
    addOutcomeField(outcome) {
      outcome.fields.push({
        id: uuidv4(),
        label: '',
      })
    },
    addSection() {
      this.getTranslationObject(this.item.l10n).sections.push({
        id: uuidv4(),
        text: '',
        background_color: '#e6d8c8',
        questions: [],
        name: '',
        outcomes: [],
        conditionals_compare: '',
        conditionals: [],
      })
    },
    addSectionQuestion(section) {
      section.questions.push({
        id: uuidv4(),
        label: '',
        type: 'Buttons',
        multiple: false,
        outcomes: [],
        answers: [],
      })
    },
    addQuestionAnswer(question) {
      question.answers.push({
        id: uuidv4(),
        label: '',
      })
    },
    removeElement(arr, index) {
      if (confirm('This will remove the item altogether. Are you sure you with to continue?')) arr.splice(index, 1)
    },
    closeModal() {
      if (this.changed === true) {
        if (confirm('Are you sure you wish to close the editor? Everything not saved will be lost!')) {
          this.$router.push(this.parent)
        }
      } else {
        this.$router.push(this.parent)
      }
    },
    copyBlocks(translation, field) {
      console.log(this.getTranslationObject(this.item.l10n, translation.toLowerCase())[field])
      if (confirm(`Are you sure you with to overwrite this content with content from ${translation}?`))
        this.getTranslationObject(this.item.l10n, this.lang)[field] = JSON.parse(
          JSON.stringify(this.getTranslationObject(this.item.l10n, translation.toLowerCase())[field])
        )
    },
  },
}
</script>
